import React, { useMemo, useRef } from 'react';
import { useChat, useRoomContext, useTranscriptions, } from '@livekit/components-react';
import { transcriptionToChatMessage } from '@/lib/utils';
export default function useChatAndTranscription() {
    const transcriptions = useTranscriptions();
    const chat = useChat();
    const room = useRoomContext();
    // Track sequence numbers for messages to maintain insertion order
    const sequenceMap = useRef(new Map());
    const nextSequence = useRef(0);
    const [history, setHistory] = React.useState([]);
    // Load history from session storage on mount
    React.useEffect(() => {
        try {
            const stored = sessionStorage.getItem('agent_transcript');
            if (stored) {
                setHistory(JSON.parse(stored));
            }
        }
        catch (e) {
            console.error('Failed to load transcript history:', e);
        }
    }, []);
    const mergedTranscriptions = useMemo(() => {
        const liveMessages = [
            ...transcriptions.map((transcription) => transcriptionToChatMessage(transcription, room)),
            ...chat.chatMessages,
        ];
        // Deduplicate: Combine history and live messages, keeping unique IDs
        // Live messages take precedence if duplicates exist (to get updates like final transcription)
        const combined = [...history];
        const historyIds = new Set(history.map((m) => m.id));
        liveMessages.forEach((msg) => {
            if (!historyIds.has(msg.id)) {
                combined.push(msg);
            }
            else {
                // Update existing item in history if matched (e.g. valid update)
                // NOTE: For simplicity, we assume live updates replace history entry if needed, but array order matters
                // A simpler approach is: History is strictly OLD sessions. detailed merge might be complex.
                // Let's rely on unique ID set from combined and filter.
                const index = combined.findIndex((h) => h.id === msg.id);
                if (index !== -1) {
                    combined[index] = msg;
                }
            }
        });
        // Assign sequence numbers to messages
        combined.forEach((msg) => {
            if (!sequenceMap.current.has(msg.id)) {
                sequenceMap.current.set(msg.id, nextSequence.current++);
            }
            msg._sequence = sequenceMap.current.get(msg.id);
        });
        // Stable sort
        return combined.sort((a, b) => {
            var _a, _b;
            const timeDiff = a.timestamp - b.timestamp;
            if (Math.abs(timeDiff) < 10) {
                return ((_a = a._sequence) !== null && _a !== void 0 ? _a : 0) - ((_b = b._sequence) !== null && _b !== void 0 ? _b : 0);
            }
            return timeDiff;
        });
    }, [transcriptions, chat.chatMessages, room, history]);
    // Save to session storage whenever merged list changes
    React.useEffect(() => {
        if (mergedTranscriptions.length > 0) {
            const latest10 = mergedTranscriptions.slice(-10);
            sessionStorage.setItem('agent_transcript', JSON.stringify(latest10));
            // sessionStorage.setItem('agent_transcript', JSON.stringify(mergedTranscriptions));
        }
    }, [mergedTranscriptions]);
    return { messages: mergedTranscriptions, send: chat.send };
}
